import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ApiSdkService } from 'src/app/services/api-sdk.service';

interface ChatMessage {
  createdAt: Date | string;
  sender: 'user' | 'assistant';
  message: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  editedAt?: Date | string;
}

interface SupportChat {
  _id: string;
  // PHASE 19: absent on ACCOUNT-scoped chats, which have no project.
  organizationId?: string;
  instanceId?: string;
  /** PHASE 19. Absent means 'project' — every pre-existing chat. */
  scope?: 'project' | 'account';
  accountUserId?: string;
  messages: ChatMessage[];
  lastMessageAt?: Date | string;
  createdAt: Date | string;
  updatedAt: Date | string;
  needsResponse?: boolean;
  markedAsResponded?: boolean;
  /** Pinned by the admin. Drives the ★ and the Favourites tab. */
  favourite?: boolean;
  userEmail?: string;
  userName?: string;
  userPlan?: string;
  /**
   * ⭐ WHICH CUSTOMER MESSAGES CAN BE TRANSLATED, AND WHICH ALREADY ARE.
   *
   * ⚠️ ADMIN-ONLY, AND IT ARRIVES ONLY FROM `GET /:chatId`. The customer's own
   * chat endpoints return the message array and nothing else, so there is no
   * response shape anywhere in which this could reach them — see
   * `adminTranslations` in the backend's chat interface.
   *
   * Absent on a chat loaded before this existed, which is why every reader
   * below tolerates `undefined` rather than assuming the array.
   */
  translationHints?: TranslationHint[];
}

/**
 * One entry per customer message worth offering a translation for.
 *
 * `canTranslate` is the backend heuristic's verdict (`support-language.ts`):
 * "written in neither Spanish nor English". `englishText` is non-null only once
 * somebody has actually asked for the translation — the click is what spends money.
 */
interface TranslationHint {
  /** The message's `createdAt`, ISO. The key that ties a hint to a message. */
  messageAt: string;
  canTranslate: boolean;
  englishText: string | null;
  sourceLanguage: string | null;
}

/**
 * ⭐ THE AI DRAFT REPLY, as the admin panel sees it.
 *
 * Written by `support-suggestion.service.ts` when the CUSTOMER sends a message,
 * grounded in totalum.app/docs, in the customer's own language. It is a draft: it
 * is never sent, and nothing here can send it — "Use this" only fills the box.
 */
interface SupportSuggestion {
  text: string;
  language: string;
  /** Only present when the reply is in neither English nor Spanish. */
  englishText: string | null;
  sources: { title: string; url: string }[];
  /** The customer message it answers. Used to tell a stale draft from a fresh one. */
  basedOnMessageAt: string;
  createdAt: string;
  model: string;
  /**
   * ⭐ How many lookups the draft needed (account, ledger, projects, prompts,
   * history). Shown beside the model, because it is the one number that says whether
   * to trust the specifics: `0` on a documentation question is correct and cheap,
   * `0` on "why was I charged" means the model answered from nothing.
   */
  toolCallsMade?: number;
}

/**
 * The admin list's tabs. A superset of the chat scopes: 'favourites' and
 * 'credit-requests' say nothing about what KIND of chat it is — see `matchesTab` in
 * the backend's `assistant-chat.service.ts`, which is where they are applied.
 */
type ChatTab = 'all' | 'project' | 'account' | 'favourites' | 'credit-requests';

const POLL_INTERVAL = 8000; // 8 seconds

@Component({
  selector: 'app-support-chat',
  templateUrl: './support-chat.page.html',
  styleUrls: ['./support-chat.page.scss']
})
export class SupportChatPage implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('messageTextarea') messageTextarea!: ElementRef;

  // Chat list
  chats: SupportChat[] = [];
  totalChats: number = 0;
  totalPages: number = 0;
  currentPage: number = 1;
  searchQuery: string = '';
  isLoadingChats: boolean = false;
  /**
   * ⭐ The `Req. credits` badge: claims still waiting on a decision. Counted server-side
   * across the whole collection, so it stays right while another tab is open.
   */
  pendingCreditRequests: number = 0;
  showUnrespondOnly: boolean = false;
  /**
   * ⭐ THE LIST TAB. Sent to the server, which filters BEFORE paging — see
   * `getAllChatsWithUnreadFilter`.
   *
   * ⚠️ IT USED TO BE CLIENT-SIDE, AND CANNOT BE ANY MORE. 'all' now HIDES the accounts
   * that have only ever sent the automated credit claim, and 'favourites' gathers from
   * the whole collection: filtering the page the browser already holds would show a
   * half-empty page of 50 and lose every pinned chat that sorted onto page 2.
   */
  scopeFilter: ChatTab = 'all';
  isMarkingResponded: boolean = false;

  // Selected chat
  selectedChat: SupportChat | null = null;

  // ── The AI draft reply ───────────────────────────────────────────────────
  suggestion: SupportSuggestion | null = null;
  suggestionUsage: { used: number; max: number } | null = null;
  isLoadingSuggestion = false;
  /**
   * Collapsed after "Use this", so the draft stops competing with the edit.
   *
   * ⚠️ IT ALSO STARTS COLLAPSED, ON EVERY DEVICE — see `loadSuggestion`. The card is
   * several lines tall and sat between the conversation and the composer on every
   * open, so the reply the agent was about to write was pushed off screen by a draft
   * they had not asked for. One click brings it back.
   */
  suggestionDismissed = true;
  /** The English translation is hidden by default; it is a check, not the reply. */
  showSuggestionEnglish = false;

  // ── Customer-message translations ────────────────────────────────────────
  /**
   * ⚠️ KEYED BY `messageAt`, NOT BY ARRAY INDEX. `deleteMessage` renumbers the
   * messages, so an index-keyed map would silently attach a translation to a
   * different message the moment anything above it was removed.
   */
  translationOpen: { [messageAt: string]: boolean } = {};
  /** Locally fetched translations, merged over whatever the hints already carried. */
  translationText: { [messageAt: string]: { englishText: string; sourceLanguage: string } } = {};
  /** The `messageAt` currently in flight, so only that one bubble shows a spinner. */
  translatingAt: string | null = null;
  translationError: { [messageAt: string]: string } = {};
  isLoadingChat: boolean = false;

  // Message input
  currentMessage: string = '';
  isSending: boolean = false;

  /**
   * ⭐ POLISH — the proofread version of what is currently in the composer.
   *
   * ⚠️ IT LIVES HERE AND NOT IN `currentMessage`, WHICH IS THE ENTIRE FEATURE. The
   * correction is shown in a modal beside the draft and only reaches the textarea if
   * the admin presses Apply. Writing it straight into `currentMessage` would destroy
   * the wording they chose, with no undo — a support reply is not a thing you silently
   * rewrite under somebody's cursor.
   */
  polishedText: string = '';
  /** The draft the correction was made from, so the modal can show both sides. */
  polishSource: string = '';
  polishLanguage: string = '';
  /** True when the model changed nothing — the modal says so rather than faking work. */
  polishUnchanged: boolean = false;
  showPolishModal: boolean = false;
  isPolishing: boolean = false;
  polishError: string = '';
  polishCopied: boolean = false;
  private polishCopiedTimer: any = null;

  attachedFileUrl: string = '';
  attachedFileName: string = '';
  /**
   * ⭐ THE TYPE THE SERVER SNIFFED FROM THE FILE'S BYTES, when the attachment came
   * from an upload. Empty for the paste-a-URL flow, which has only a name to go on.
   *
   * ⚠️ IT IS CARRIED RATHER THAN RE-DERIVED because the URL cannot be trusted to
   * carry it: a signed storage link ends in a query string, not in `.png`. See
   * `getFileTypeFromUrl` for the bug that cost every attachment its preview.
   */
  attachedFileType: string = '';
  showFileUrlInput: boolean = false;

  // ── Uploading an attachment ──────────────────────────────────────────────
  /**
   * ⭐ The agent can now pick a file instead of hosting one themselves and pasting
   * a link. The upload produces a public URL and nothing else — no database record,
   * no link to any project — and then rides the SAME `attachedFileUrl` the paste-a-URL
   * flow always used, so `sendMessage` did not change at all.
   */
  isUploadingFile = false;
  uploadError: string | null = null;
  /** Mirrors the server's allow-list, for the picker's own filter. */
  readonly uploadAccept = 'image/png,image/jpeg,image/gif,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  // Edit/Delete state
  editingMessageIndex: number | null = null;
  editingMessageContent: string = '';
  isEditing: boolean = false;
  isDeleting: boolean = false;

  // Credits state
  userCredits: number | null = null;
  isLoadingCredits: boolean = false;
  isGivingCredits: boolean = false;
  showGiveCreditsModal: boolean = false;
  showGiveCreditsConfirm: boolean = false;
  creditsToGive: number = 0;
  creditAmounts: number[] = [50, 100, 200, 500, 1000, 5000];
  customCredits: number | null = null;
  readonly MIN_CUSTOM_CREDITS = 1;
  readonly MAX_CUSTOM_CREDITS = 500000;
  // Remove-credits (subtle, hidden by default) state
  creditsMode: 'give' | 'remove' = 'give';
  showRemoveCreditsSection: boolean = false;
  customRemoveCredits: number | null = null;

  // Create new chat state
  showCreateChatModal: boolean = false;
  organizationsWithoutChats: { organizationId: string; plan?: string }[] = [];
  isLoadingOrganizations: boolean = false;
  organizationSearchQuery: string = '';
  selectedOrganizationId: string = '';
  newChatInitialMessage: string = '';
  isCreatingChat: boolean = false;

  /**
   * ⭐ THE CUSTOMER PANEL — the same slide-over the CEO users page opens on a row.
   *
   * Set to an account user's id to open it, `null` to close it; that is the whole
   * contract of `app-ceo-user-detail`.
   */
  detailUserId: string | null = null;

  private pollInterval: any = null;
  private chatListPollInterval: any = null;
  private readonly ADMIN_EMAIL = 'speedparadigm@gmail.com';
  private previousMessageCount: number = 0;
  private isUserNearBottom: boolean = true;

  private readonly CHAT_LIST_POLL_INTERVAL = 20000; // 20 seconds

  constructor(
    private apiSdkService: ApiSdkService,
    private router: Router
  ) {}

  async ngOnInit() {
    // Check if user is admin
    const userData = await this.apiSdkService.getFullUserData();
    if (userData?.success?.data?.email !== this.ADMIN_EMAIL) {
      this.router.navigate(['/account']);
      return;
    }

    this.loadChats();
    this.startChatListPolling();
  }

  ngOnDestroy() {
    this.stopPolling();
    this.stopChatListPolling();
  }

  startChatListPolling() {
    this.chatListPollInterval = setInterval(async () => {
      await this.pollChatList();
    }, this.CHAT_LIST_POLL_INTERVAL);
  }

  stopChatListPolling() {
    if (this.chatListPollInterval) {
      clearInterval(this.chatListPollInterval);
      this.chatListPollInterval = null;
    }
  }

  private async pollChatList() {
    try {
      const response = await this.apiSdkService.getAllSupportChats(
        this.currentPage,
        50,
        this.searchQuery || undefined,
        this.showUnrespondOnly,
        this.scopeFilter
      );

      if (response?.success?.data) {
        const newChats = response.success.data.chats || [];

        this.pendingCreditRequests = response.success.data.creditRequestsPending || 0;

        // Check if there are changes
        if (this.hasChatListChanged(newChats)) {
          this.chats = newChats;
          this.totalChats = response.success.data.total || 0;
          this.totalPages = response.success.data.pages || 0;
        }
      }
    } catch (error) {
      console.error('Error polling chat list:', error);
    }
  }

  private hasChatListChanged(newChats: SupportChat[]): boolean {
    // Different count
    if (newChats.length !== this.chats.length) {
      return true;
    }

    // Check each chat for changes
    for (let i = 0; i < newChats.length; i++) {
      const newChat = newChats[i];
      const oldChat = this.chats[i];

      // Different chat ID at same position (order changed)
      if (newChat._id !== oldChat._id) {
        return true;
      }

      // Different needsResponse status
      if (newChat.needsResponse !== oldChat.needsResponse) {
        return true;
      }

      // Different message count
      if ((newChat.messages?.length || 0) !== (oldChat.messages?.length || 0)) {
        return true;
      }

      // Different lastMessageAt
      if (new Date(newChat.lastMessageAt || 0).getTime() !== new Date(oldChat.lastMessageAt || 0).getTime()) {
        return true;
      }
    }

    return false;
  }

  async loadChats() {
    try {
      this.isLoadingChats = true;
      const response = await this.apiSdkService.getAllSupportChats(
        this.currentPage,
        50,
        this.searchQuery || undefined,
        this.showUnrespondOnly,
        this.scopeFilter
      );

      if (response?.success?.data) {
        this.chats = response.success.data.chats || [];
        this.totalChats = response.success.data.total || 0;
        this.totalPages = response.success.data.pages || 0;
        this.pendingCreditRequests = response.success.data.creditRequestsPending || 0;
      }
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      this.isLoadingChats = false;
    }
  }

  /**
   * PHASE 19 — what to call a chat in the list.
   *
   * An ACCOUNT chat has no project, so it is labelled by the person instead. The
   * `|| 'Account'` fallback matters: a chat created before the user's email was
   * stamped would otherwise render an empty row.
   */
  chatLabel(chat: SupportChat): string {
    if (chat?.scope === 'account') {
      return chat.userName || chat.userEmail || 'Account';
    }
    return chat?.organizationId || '';
  }

  /** PHASE 19. Absent scope means 'project' — see the model note. */
  isAccountChat(chat: SupportChat): boolean {
    return chat?.scope === 'account';
  }

  /**
   * ⚠️⚠️ THE PLAN IS SPELT `'profesional'`, WITH ONE `s`, EVERYWHERE IN THIS CODEBASE —
   * `PlansI`, the Stripe metadata, the CEO pages' `planStyle`. This badge used to test
   * for `'professional'`, which nothing ever writes, so the branch was dead and the plan
   * matched no background rule at all: that is why a Professional customer's badge
   * rendered as white-on-white rather than as its colour.
   *
   * The English spelling is tolerated too, so this keeps working if the value is ever
   * corrected.
   */
  isProfesionalPlan(plan?: string): boolean {
    return plan === 'profesional' || plan === 'professional';
  }

  /** The tabs, in the order they are drawn. */
  readonly tabs: { id: ChatTab; label: string; icon?: string; title: string }[] = [
    { id: 'all', label: 'All', title: 'Everyone who has actually written to support' },
    { id: 'project', label: 'Projects', title: 'Project chats' },
    { id: 'account', label: 'Accounts', title: 'Account chats (Totalum Platform)' },
    {
      id: 'credit-requests',
      label: 'Req. credits',
      title: 'Accounts whose only messages are automated credit requests for a shared post',
    },
    { id: 'favourites', label: '', icon: 'fa-star', title: 'Pinned chats' },
  ];

  /**
   * ⚠️ IT RELOADS RATHER THAN RE-SLICING, and goes back to page 1: each tab is its own
   * list with its own page count now, so keeping the page number would land on a page
   * the new tab may not have.
   */
  setScopeFilter(scope: ChatTab) {
    if (this.scopeFilter === scope) return;
    this.scopeFilter = scope;
    this.currentPage = 1;
    this.loadChats();
  }

  /**
   * ⭐ PIN A CHAT, OR UNPIN IT.
   *
   * ⚠️ THE STAR MOVES FIRST AND IS PUT BACK IF THE CALL FAILS. A pin is a one-tap
   * gesture, often on a phone; waiting a round-trip before the star fills reads as the
   * tap not landing, and the retry double-toggles.
   *
   * ⚠️ BOTH COPIES OF THE CHAT ARE UPDATED. The open conversation is replaced by the
   * `/:chatId` document as soon as a message arrives, so it is a different object from
   * the list row — set one and the other star silently disagrees.
   */
  async toggleFavourite(chat: SupportChat, event?: Event) {
    // The row itself opens the chat; the star must not.
    event?.stopPropagation();
    if (!chat) return;

    const favourite = !chat.favourite;
    this.applyFavourite(chat._id, favourite);

    try {
      await this.apiSdkService.setSupportChatFavourite(chat._id, favourite);

      // Unpinned while looking at the pinned list: the row no longer belongs here.
      if (!favourite && this.scopeFilter === 'favourites') {
        this.loadChats();
      }
    } catch (error) {
      console.error('Error updating chat favourite:', error);
      this.applyFavourite(chat._id, !favourite);
    }
  }

  private applyFavourite(chatId: string, favourite: boolean) {
    const chatInList = this.chats.find(c => c._id === chatId);
    if (chatInList) chatInList.favourite = favourite;
    if (this.selectedChat?._id === chatId) this.selectedChat.favourite = favourite;
  }

  toggleUnrespondFilter() {
    this.showUnrespondOnly = !this.showUnrespondOnly;
    this.currentPage = 1;
    this.loadChats();
  }

  async selectChat(chat: SupportChat) {
    // ⚠️ The panel belongs to the chat it was opened from. Leaving it up while the
    // conversation changes would show one customer's account beside another's words.
    this.detailUserId = null;
    this.selectedChat = chat;
    this.previousMessageCount = chat.messages?.length || 0;
    this.isUserNearBottom = true;
    this.userCredits = null;
    this.stopPolling();
    await this.loadChatMessages();
    this.startPolling();

    // Always scroll to bottom when selecting a chat
    setTimeout(() => this.scrollToBottom(), 150);

    // Mark chat as seen when opening
    this.markChatAsSeen(chat);

    // Load user credits
    this.loadUserCredits();

    // ⚠️ ON OPEN, NOT ON POLL. Re-reading the draft every 8 seconds would move the
    // text under an admin who is part-way through copying it, and the backend only
    // ever writes one when the CUSTOMER writes — so there is nothing new to see
    // until they do.
    this.loadSuggestion();
  }

  async loadSuggestion() {
    if (!this.selectedChat) return;

    this.suggestion = null;
    // ⚠️ COLLAPSED, NOT OPEN. The draft is offered, never imposed — see the field.
    this.suggestionDismissed = true;
    this.showSuggestionEnglish = false;

    try {
      this.isLoadingSuggestion = true;
      const response = await this.apiSdkService.getSupportChatSuggestion(this.selectedChat._id);
      const data = response?.success?.data;
      this.suggestion = data?.suggestion || null;
      this.suggestionUsage = data?.usage || null;
    } catch (error) {
      // A missing draft is the ordinary state. Never surface it as an error.
      console.error('Error loading the reply suggestion:', error);
      this.suggestion = null;
    } finally {
      this.isLoadingSuggestion = false;
    }
  }

  /**
   * ⚠️ IT FILLS THE BOX; IT DOES NOT SEND. The whole point of a suggestion is that
   * a human reads it first, so this stops one step short of the thing that would
   * make it a bot. The draft collapses afterwards so the admin is editing their
   * reply rather than comparing it to a copy of itself.
   */
  useSuggestion() {
    if (!this.suggestion) return;

    this.currentMessage = this.suggestion.text;
    this.suggestionDismissed = true;

    setTimeout(() => {
      this.autoResizeTextarea();
      this.messageTextarea?.nativeElement?.focus();
    }, 0);
  }

  /**
   * ⭐ DISCARD — this draft is wrong, do not offer it again.
   *
   * ⚠️ IT WRITES TO THE SERVER, WHICH IS THE WHOLE DIFFERENCE FROM THE OLD "×".
   * That one set a component flag, so a draft an agent had already judged wrong
   * came back on the next page load — on the conversation they were most likely
   * to reopen. This one is permanent for that draft, on every device.
   *
   * ⚠️ THE CARD GOES AWAY IMMEDIATELY AND STAYS AWAY EVEN IF THE CALL FAILS.
   * `suggestion = null` is the honest local outcome of "I don't want this": a
   * draft that reappears because a request 500'd would read as the button not
   * working, and the next reload settles it either way.
   */
  async discardSuggestion() {
    if (!this.selectedChat || !this.suggestion) return;

    this.suggestion = null;
    this.suggestionDismissed = false;

    try {
      await this.apiSdkService.discardSupportChatSuggestion(this.selectedChat._id);
    } catch (error) {
      console.error('Error discarding the reply suggestion:', error);
    }
  }

  /**
   * ⭐ POLISH — proofread the draft in the composer.
   *
   * ⚠️ IT OPENS A MODAL; IT DOES NOT TOUCH THE TEXTAREA. `polishedText` is a
   * separate field precisely so this cannot overwrite what the admin typed. Only
   * `applyPolish()` writes to `currentMessage`, and only on an explicit click.
   *
   * The modal opens immediately, in its loading state, so the admin can see the
   * call is happening and can close it while it is in flight.
   */
  async polishMessage() {
    const draft = this.currentMessage.trim();
    if (!draft || this.isPolishing) return;

    this.showPolishModal = true;
    this.isPolishing = true;
    this.polishError = '';
    this.polishedText = '';
    this.polishUnchanged = false;
    this.polishCopied = false;
    this.polishSource = draft;

    try {
      /*
        ⚠️ THE CHAT ID GOES WITH IT. The server then reads what the customer
        actually asked and what they are called, so the polish can tell whether
        the draft reads as an answer to that question instead of proofreading it
        blind. It never sends the context itself — see the SDK method.
      */
      const response: any = await this.apiSdkService.polishSupportChatDraft(
        draft,
        this.selectedChat?._id,
      );
      const data = response?.success?.data;

      const polished = (data?.polishedText || '').trim();
      if (!polished) throw new Error('Empty response');

      this.polishedText = polished;
      this.polishLanguage = data?.language || '';
      this.polishUnchanged = !!data?.unchanged;
    } catch (error: any) {
      console.error('Error polishing the draft:', error);
      this.polishError =
        error?.error?.errors?.errorMessage || error?.message || 'Could not polish that text.';
    } finally {
      this.isPolishing = false;
    }
  }

  /**
   * Replace the composer contents with the corrected text.
   *
   * ⚠️ THE ONLY WRITER OF `currentMessage` IN THE POLISH FLOW, and it runs on a
   * click and nothing else.
   */
  applyPolish() {
    if (!this.polishedText) return;

    this.currentMessage = this.polishedText;
    this.closePolishModal();

    setTimeout(() => {
      this.autoResizeTextarea();
      this.messageTextarea?.nativeElement?.focus();
    }, 0);
  }

  async copyPolish() {
    if (!this.polishedText) return;

    try {
      await navigator.clipboard.writeText(this.polishedText);
      this.polishCopied = true;
      clearTimeout(this.polishCopiedTimer);
      this.polishCopiedTimer = setTimeout(() => (this.polishCopied = false), 2000);
    } catch (error) {
      console.error('Could not copy the polished text:', error);
    }
  }

  /** ⚠️ Closing DISCARDS the correction. The draft in the textarea is untouched. */
  closePolishModal() {
    this.showPolishModal = false;
    this.polishError = '';
    this.polishCopied = false;
    clearTimeout(this.polishCopiedTimer);
  }

  /**
   * True when the customer has written again since the draft was made — so the
   * draft answers a message that is no longer the last one.
   */
  get isSuggestionStale(): boolean {
    if (!this.suggestion || !this.selectedChat?.messages?.length) return false;

    const messages = this.selectedChat.messages;
    const lastUser = [...messages].reverse().find(m => m.sender === 'user');
    if (!lastUser) return false;

    return new Date(lastUser.createdAt).getTime() !== new Date(this.suggestion.basedOnMessageAt).getTime();
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ⭐ TRANSLATING A CUSTOMER MESSAGE
  //
  //  The agent reads Spanish and English. When a customer writes in anything else,
  //  the bubble becomes clickable and one click shows the message in English.
  //
  //  ⚠️ ON CLICK, NEVER AUTOMATICALLY. Translating every foreign message on open
  //  would bill a model call per message per page load, for messages nobody may
  //  read. The heuristic that decides whether to OFFER the click is free and runs
  //  server-side; the translation itself costs money and waits to be asked for.
  //
  //  ⚠️ NOTHING HERE IS EVER SENT OR SHOWN TO THE CUSTOMER. The translation lives
  //  on the chat document under `adminTranslations`, and the customer's own chat
  //  endpoints return only the message array.
  // ═══════════════════════════════════════════════════════════════════════

  private hintFor(message: ChatMessage): TranslationHint | undefined {
    const at = new Date(message.createdAt).toISOString();
    return (this.selectedChat?.translationHints || []).find(h => h.messageAt === at);
  }

  /** The map key for a message. ISO, so it matches what the backend sent. */
  messageKey(message: ChatMessage): string {
    return new Date(message.createdAt).toISOString();
  }

  /**
   * Should this bubble offer a translation?
   *
   * ⚠️ CUSTOMER MESSAGES ONLY, and only when the backend said so. Our own replies
   * were written by the person reading the screen.
   */
  canTranslate(message: ChatMessage): boolean {
    if (message.sender !== 'user') return false;
    const hint = this.hintFor(message);
    if (!hint) return false;
    // A message already translated keeps its control even if the heuristic would
    // no longer offer one — the agent asked for it and may want to re-open it.
    return hint.canTranslate || !!this.translationOf(message);
  }

  /** The translation, from this session's fetches or from the hint's cache. */
  translationOf(message: ChatMessage): { englishText: string; sourceLanguage: string } | null {
    const key = this.messageKey(message);
    if (this.translationText[key]) return this.translationText[key];

    const hint = this.hintFor(message);
    if (hint?.englishText) {
      return { englishText: hint.englishText, sourceLanguage: hint.sourceLanguage || '' };
    }
    return null;
  }

  isTranslationOpen(message: ChatMessage): boolean {
    return !!this.translationOpen[this.messageKey(message)];
  }

  isTranslating(message: ChatMessage): boolean {
    return this.translatingAt === this.messageKey(message);
  }

  translationErrorFor(message: ChatMessage): string | null {
    return this.translationError[this.messageKey(message)] || null;
  }

  /**
   * The click. Opens the translation, fetching it the first time.
   *
   * ⚠️ A SECOND CLICK COLLAPSES IT RATHER THAN RE-FETCHING. Re-billing a
   * translation because somebody clicked twice would be the obvious way to make
   * this expensive; the backend caches too, but the cheapest call is the one not
   * made.
   */
  async toggleTranslation(message: ChatMessage) {
    if (!this.selectedChat || !this.canTranslate(message)) return;

    const key = this.messageKey(message);

    if (this.translationOpen[key]) {
      this.translationOpen[key] = false;
      return;
    }

    // Already have the text — just show it.
    if (this.translationOf(message)) {
      this.translationOpen[key] = true;
      return;
    }

    if (this.translatingAt) return; // one at a time; the spinner says which

    this.translatingAt = key;
    delete this.translationError[key];

    try {
      const response = await this.apiSdkService.translateSupportChatMessage(this.selectedChat._id, key);
      const translation = response?.success?.data?.translation;

      if (translation?.englishText) {
        this.translationText[key] = {
          englishText: translation.englishText,
          sourceLanguage: translation.sourceLanguage || '',
        };
        this.translationOpen[key] = true;
      } else {
        this.translationError[key] = 'No translation came back. Try again.';
      }
    } catch (error: any) {
      // Shown on the bubble, not as a toast: it belongs to the message that failed.
      console.error('Error translating a support message:', error);
      this.translationError[key] =
        error?.error?.errors?.errorMessage || error?.message || 'Could not translate this message.';
    } finally {
      this.translatingAt = null;
    }
  }

  /**
   * Open the picker. The input is hidden — a styled button is the affordance.
   */
  openFilePicker() {
    this.uploadError = null;
    this.fileInput?.nativeElement?.click();
  }

  /**
   * Upload the chosen file and attach the URL it produces.
   *
   * ⚠️ THE INPUT IS RESET AT THE END, ALWAYS. Without it, picking the same file twice
   * in a row fires no `change` event the second time — so a failed upload could not be
   * retried with the same file, which is exactly when someone would want to.
   *
   * ⚠️ `accept` ON THE INPUT IS A CONVENIENCE, NOT THE CHECK. The server decides from
   * the file's bytes; a user can always pick "all files" past a browser filter.
   */
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;

    this.isUploadingFile = true;
    this.uploadError = null;

    try {
      const response = await this.apiSdkService.uploadSupportChatFile(file);
      const data = response?.success?.data;

      if (data?.fileUrl) {
        this.attachedFileUrl = data.fileUrl;
        this.attachedFileName = data.fileName || file.name;
        // The server decided this from the bytes; `file.type` is only the browser's
        // claim and the URL cannot be read for it. See `attachedFileType`.
        this.attachedFileType = data.fileType || '';
        // The URL panel is for pasting links; a successful upload has nothing to paste.
        this.showFileUrlInput = false;
      } else {
        /*
          The backend answers a refusal with a readable sentence ("that file type is
          not allowed"), so it is shown as-is rather than replaced with a generic one.
        */
        this.uploadError =
          response?.success?.errors?.errorMessage ||
          response?.error?.errors?.errorMessage ||
          'Could not upload that file.';
      }
    } catch (error) {
      console.error('Error uploading a support attachment:', error);
      this.uploadError = 'Could not upload that file.';
    } finally {
      this.isUploadingFile = false;
      input.value = '';
    }
  }

  async loadUserCredits() {
    if (!this.selectedChat) return;

    try {
      this.isLoadingCredits = true;
      const response = await this.apiSdkService.getChatUserCredits(this.selectedChat._id);
      if (response?.success?.data) {
        this.userCredits = response.success.data.credits;
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
      this.userCredits = null;
    } finally {
      this.isLoadingCredits = false;
    }
  }

  // Sanitize an amount to a whole number within the allowed range (no decimals)
  private sanitizeCreditsInput(value: any): number | null {
    if (value === null || value === undefined || value === '' || isNaN(value)) {
      return null;
    }
    let amount = Math.floor(Number(value));
    if (amount > this.MAX_CUSTOM_CREDITS) amount = this.MAX_CUSTOM_CREDITS;
    if (amount < 0) amount = 0;
    return amount;
  }

  private isAmountValid(value: number | null): boolean {
    return (
      value !== null &&
      Number.isInteger(value) &&
      value >= this.MIN_CUSTOM_CREDITS &&
      value <= this.MAX_CUSTOM_CREDITS
    );
  }

  openGiveCreditsModal() {
    this.creditsMode = 'give';
    this.showRemoveCreditsSection = false;
    this.customCredits = null;
    this.customRemoveCredits = null;
    this.showGiveCreditsModal = true;
  }

  closeGiveCreditsModal() {
    this.showGiveCreditsModal = false;
    this.showRemoveCreditsSection = false;
    this.customCredits = null;
    this.customRemoveCredits = null;
  }

  openGiveCreditsConfirm(amount: number) {
    this.creditsMode = 'give';
    this.creditsToGive = amount;
    this.showGiveCreditsConfirm = true;
  }

  // --- Give (custom amount) ---
  onCustomCreditsChange(value: any) {
    this.customCredits = this.sanitizeCreditsInput(value);
  }

  get isCustomCreditsValid(): boolean {
    return this.isAmountValid(this.customCredits);
  }

  // Custom amount goes through the same confirmation step as the fixed packs
  openGiveCustomCreditsConfirm() {
    if (!this.isCustomCreditsValid || this.customCredits === null) return;
    this.openGiveCreditsConfirm(this.customCredits);
    this.showGiveCreditsModal = false;
  }

  // --- Remove (custom amount) ---
  onCustomRemoveCreditsChange(value: any) {
    this.customRemoveCredits = this.sanitizeCreditsInput(value);
  }

  get isCustomRemoveCreditsValid(): boolean {
    return this.isAmountValid(this.customRemoveCredits);
  }

  openRemoveCreditsConfirm() {
    if (!this.isCustomRemoveCreditsValid || this.customRemoveCredits === null) return;
    this.creditsMode = 'remove';
    this.creditsToGive = this.customRemoveCredits;
    this.showGiveCreditsConfirm = true;
    this.showGiveCreditsModal = false;
  }

  cancelGiveCredits() {
    this.showGiveCreditsConfirm = false;
    this.creditsToGive = 0;
    this.customCredits = null;
    this.customRemoveCredits = null;
    this.creditsMode = 'give';
  }

  async confirmGiveCredits() {
    if (!this.selectedChat || !this.creditsToGive || this.isGivingCredits) return;

    try {
      this.isGivingCredits = true;
      const response =
        this.creditsMode === 'remove'
          ? await this.apiSdkService.removeCreditsFromChatUser(this.selectedChat._id, this.creditsToGive)
          : await this.apiSdkService.giveCreditsToChatUser(this.selectedChat._id, this.creditsToGive);
      if (response?.success?.data) {
        this.userCredits = response.success.data.newBalance;
      }
      this.showGiveCreditsConfirm = false;
      this.creditsToGive = 0;
      this.customCredits = null;
      this.customRemoveCredits = null;
      this.showRemoveCreditsSection = false;
      this.creditsMode = 'give';
    } catch (error) {
      console.error('Error updating credits:', error);
    } finally {
      this.isGivingCredits = false;
    }
  }

  private async markChatAsSeen(chat: SupportChat) {
    try {
      await this.apiSdkService.markSupportChatAsSeen(chat._id);
    } catch (error) {
      console.warn('Error marking chat as seen:', error);
    }
  }

  async markAsResponded() {
    if (!this.selectedChat || this.isMarkingResponded) return;

    try {
      this.isMarkingResponded = true;
      await this.apiSdkService.markSupportChatAsResponded(this.selectedChat._id);

      // Update local state
      this.selectedChat.needsResponse = false;
      this.selectedChat.markedAsResponded = true;

      // Reload the entire chat list to reflect changes
      await this.loadChats();
    } catch (error) {
      console.error('Error marking chat as responded:', error);
    } finally {
      this.isMarkingResponded = false;
    }
  }

  async loadChatMessages() {
    if (!this.selectedChat) return;

    try {
      this.isLoadingChat = this.selectedChat.messages?.length === 0;
      this.checkIfUserNearBottom();

      const response = await this.apiSdkService.getSupportChatById(this.selectedChat._id);

      if (response?.success?.data) {
        const newChat = response.success.data;
        const currentMessages = this.selectedChat.messages || [];
        const newMessages = newChat.messages || [];

        // Only update if there are actually new messages (compare count and last message time)
        const hasNewMessages = newMessages.length !== currentMessages.length ||
          (newMessages.length > 0 && currentMessages.length > 0 &&
           new Date(newMessages[newMessages.length - 1]?.createdAt).getTime() !==
           new Date(currentMessages[currentMessages.length - 1]?.createdAt).getTime());

        if (hasNewMessages || currentMessages.length === 0) {
          this.selectedChat = newChat;
          this.previousMessageCount = newMessages.length;

          // Auto-scroll if user is near bottom or there are new messages
          if (this.isUserNearBottom || hasNewMessages) {
            setTimeout(() => this.scrollToBottom(), 100);
          }
        }
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
    } finally {
      this.isLoadingChat = false;
    }
  }

  checkIfUserNearBottom(): void {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      const threshold = 100; // pixels from bottom
      this.isUserNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    }
  }

  startPolling() {
    if (!this.selectedChat) return;

    this.pollInterval = setInterval(async () => {
      if (this.selectedChat) {
        this.checkIfUserNearBottom();
        const response = await this.apiSdkService.getSupportChatById(this.selectedChat._id);
        if (response?.success?.data) {
          const newChat = response.success.data;
          const currentMessages = this.selectedChat.messages || [];
          const newMessages = newChat.messages || [];

          // Only update if there are actually new messages
          const hasNewMessages = newMessages.length !== currentMessages.length ||
            (newMessages.length > 0 && currentMessages.length > 0 &&
             new Date(newMessages[newMessages.length - 1]?.createdAt).getTime() !==
             new Date(currentMessages[currentMessages.length - 1]?.createdAt).getTime());

          if (hasNewMessages) {
            this.selectedChat = newChat;
            this.previousMessageCount = newMessages.length;

            // Auto-scroll if user is near bottom
            if (this.isUserNearBottom) {
              setTimeout(() => this.scrollToBottom(), 100);
            }
          }
        }
      }
    }, POLL_INTERVAL);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async sendMessage() {
    if ((!this.currentMessage.trim() && !this.attachedFileUrl) || !this.selectedChat || this.isSending) {
      return;
    }

    try {
      this.isSending = true;

      /*
        ⚠️ REMEMBERED BEFORE THE SEND. Once the reply lands, the acknowledgement is no
        longer the last message and `isAwaitingClaimDecision` is false — so this is the
        last moment at which the list row can be known to be showing a stale red dot.
      */
      const wasClaimPending = this.isClaimPending;

      let fileUrl: string | undefined;
      let fileName: string | undefined;
      let fileType: string | undefined;

      if (this.attachedFileUrl) {
        fileUrl = this.attachedFileUrl;
        fileName = this.attachedFileName || this.getFileNameFromUrl(this.attachedFileUrl);
        /*
          ⚠️ THE UPLOAD'S OWN TYPE FIRST. Deriving it from the URL is the fallback
          for a pasted link — for an uploaded file the server already sniffed the
          bytes, and that answer is better than any guess from a name.
        */
        fileType = this.attachedFileType || this.getFileTypeFromUrl(this.attachedFileUrl);
      }

      const response = await this.apiSdkService.sendSupportChatMessage(
        this.selectedChat._id,
        this.currentMessage.trim() || 'Attached file',
        fileUrl,
        fileName,
        fileType
      );

      if (response?.success?.data) {
        this.currentMessage = '';
        this.resetTextareaHeight();
        this.clearFileAttachment();

        // Re-fetch messages to get the updated state including the sent message
        await this.loadChatMessages();

        // Update needs response state since we just sent a message as assistant
        this.selectedChat!.needsResponse = false;
        const chatInList = this.chats.find(c => c._id === this.selectedChat!._id);
        if (chatInList) {
          chatInList.needsResponse = false;
        }

        /*
          ⚠️ THE LIST ROW HOLDS ITS OWN COPY OF THE MESSAGES, and a claim row is red
          because of what that copy ENDS with — clearing `needsResponse` on it is not
          enough. Refreshing is the honest fix, and it only costs a request on the rare
          send that answered a credit request; every other send leaves the list alone,
          as before.
        */
        if (wasClaimPending) {
          this.loadChats();
        }

        setTimeout(() => {
          this.scrollToBottom();
          // Focus the textarea after sending
          this.focusTextarea();
        }, 100);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      this.isSending = false;
      // Always focus textarea after send attempt
      this.focusTextarea();
    }
  }

  focusTextarea() {
    if (this.messageTextarea?.nativeElement) {
      this.messageTextarea.nativeElement.focus();
    }
  }

  toggleFileUrlInput() {
    this.showFileUrlInput = !this.showFileUrlInput;
    if (!this.showFileUrlInput) {
      this.clearFileAttachment();
    }
  }

  clearFileAttachment() {
    this.attachedFileUrl = '';
    this.attachedFileName = '';
    this.attachedFileType = '';
    this.showFileUrlInput = false;
  }

  getFileNameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.split('/').pop() || 'file';
    } catch {
      return 'file';
    }
  }

  /**
   * ⚠️⚠️ THE EXTENSION COMES FROM THE **PATH**, NOT FROM THE WHOLE URL.
   *
   * This is why every attachment reached the customer as a grey "File" card instead
   * of a picture. Storage links are SIGNED, so the URL does not end at the filename:
   *
   *   …/support-attachment-1786000169979-pa8ou5ao.png?GoogleAccessId=firebase-adminsdk
   *     -f9ecg%40speed-paradigm-web.iam.gserviceaccount.com&Expires=…&Signature=…
   *
   * `url.split('.').pop()` on that returns the tail of the QUERY STRING —
   * `com&Expires=…&Signature=…` — which is in no type map, so every upload was sent
   * as `application/octet-stream`. The chat decides whether to render an `<img>`
   * from `fileType`, so the picture never appeared.
   *
   * ⚠️ IT IS ALSO ONLY A FALLBACK NOW. An uploaded file carries the type the SERVER
   * sniffed from its bytes (`attachedFileType`); guessing from the name is for the
   * paste-a-URL flow, where a name is all there is.
   */
  getFileTypeFromUrl(url: string): string {
    let path = url;
    try {
      // Strips the query and the fragment. Relative or malformed input keeps the
      // raw string, which is no worse than what it had before.
      path = new URL(url).pathname;
    } catch {
      path = url.split(/[?#]/)[0];
    }

    const ext = path.split('.').pop()?.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return typeMap[ext || ''] || 'application/octet-stream';
  }

  onSearch() {
    this.currentPage = 1;
    this.loadChats();
  }

  clearSearch() {
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadChats();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadChats();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  scrollToBottom() {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }

  /**
   * Grows the composer with its content.
   * The textarea starts at a single row so it takes the least screen possible on a phone;
   * without this it would stay one row no matter how long the reply gets. The CSS max-height
   * caps the growth so the conversation is never swallowed.
   */
  autoResizeTextarea() {
    const el = this.messageTextarea?.nativeElement;
    if (!el) return;
    // Reset first, otherwise scrollHeight only ever ratchets upwards.
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  /** Returns the composer to one row (after send / clear). */
  resetTextareaHeight() {
    const el = this.messageTextarea?.nativeElement;
    if (el) {
      el.style.height = 'auto';
    }
  }

  formatTime(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatDayOnly(date: Date | string): string {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  }

  shouldShowDaySeparator(index: number): boolean {
    if (!this.selectedChat?.messages) return false;
    if (index === 0) return true;

    const currentMsg = this.selectedChat.messages[index];
    const prevMsg = this.selectedChat.messages[index - 1];

    const currentDate = new Date(currentMsg.createdAt).toDateString();
    const prevDate = new Date(prevMsg.createdAt).toDateString();

    return currentDate !== prevDate;
  }

  getMessageDay(index: number): string {
    if (!this.selectedChat?.messages || !this.selectedChat.messages[index]) return '';
    return this.formatDayOnly(this.selectedChat.messages[index].createdAt);
  }

  getLastMessage(chat: SupportChat): string {
    if (!chat.messages || chat.messages.length === 0) {
      return 'No messages';
    }
    const lastMsg = chat.messages[chat.messages.length - 1];
    return lastMsg.message.substring(0, 50) + (lastMsg.message.length > 50 ? '...' : '');
  }

  getLastMessageTime(chat: SupportChat): string {
    if (chat.lastMessageAt) {
      return this.formatDate(chat.lastMessageAt);
    }
    if (chat.messages && chat.messages.length > 0) {
      return this.formatDate(chat.messages[chat.messages.length - 1].createdAt);
    }
    return this.formatDate(chat.createdAt);
  }

  backToList() {
    this.selectedChat = null;
    this.detailUserId = null;
    this.stopPolling();
  }

  isImageFile(fileType?: string): boolean {
    return fileType?.startsWith('image/') || false;
  }

  getFileIcon(fileType?: string): string {
    if (fileType?.startsWith('image/')) return 'image';
    if (fileType?.includes('pdf')) return 'document-text';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return 'grid';
    return 'document';
  }

  downloadFile(url?: string, fileName?: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // Edit message methods
  startEditMessage(index: number, message: ChatMessage) {
    this.editingMessageIndex = index;
    this.editingMessageContent = message.message;
  }

  cancelEdit() {
    this.editingMessageIndex = null;
    this.editingMessageContent = '';
  }

  async saveEdit() {
    if (!this.selectedChat || this.editingMessageIndex === null || !this.editingMessageContent.trim()) {
      return;
    }

    try {
      this.isEditing = true;
      const response = await this.apiSdkService.editSupportChatMessage(
        this.selectedChat._id,
        this.editingMessageIndex,
        this.editingMessageContent.trim()
      );

      if (response?.success?.data) {
        // Update local message
        this.selectedChat.messages[this.editingMessageIndex] = response.success.data;
        this.cancelEdit();
      }
    } catch (error) {
      console.error('Error editing message:', error);
    } finally {
      this.isEditing = false;
    }
  }

  async deleteMessage(index: number) {
    if (!this.selectedChat || !confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      this.isDeleting = true;
      const response = await this.apiSdkService.deleteSupportChatMessage(
        this.selectedChat._id,
        index
      );

      if (response?.success) {
        // Remove from local array
        this.selectedChat.messages.splice(index, 1);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    } finally {
      this.isDeleting = false;
    }
  }

  onEditKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.cancelEdit();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEdit();
    }
  }

  // Create new chat methods
  async openCreateChatModal() {
    this.showCreateChatModal = true;
    this.organizationSearchQuery = '';
    this.selectedOrganizationId = '';
    this.newChatInitialMessage = '';
    await this.loadOrganizationsWithoutChats();
  }

  closeCreateChatModal() {
    this.showCreateChatModal = false;
    this.organizationsWithoutChats = [];
    this.organizationSearchQuery = '';
    this.selectedOrganizationId = '';
    this.newChatInitialMessage = '';
  }

  async loadOrganizationsWithoutChats() {
    try {
      this.isLoadingOrganizations = true;
      const response = await this.apiSdkService.getOrganizationsWithoutChats(
        this.organizationSearchQuery || undefined
      );

      if (response?.success?.data) {
        this.organizationsWithoutChats = response.success.data;
      }
    } catch (error) {
      console.error('Error loading organizations without chats:', error);
    } finally {
      this.isLoadingOrganizations = false;
    }
  }

  onOrganizationSearch() {
    this.loadOrganizationsWithoutChats();
  }

  selectOrganization(organizationId: string) {
    this.selectedOrganizationId = organizationId;
  }

  async createNewChat() {
    if (!this.selectedOrganizationId || this.isCreatingChat) return;

    try {
      this.isCreatingChat = true;
      const response = await this.apiSdkService.createSupportChat(
        this.selectedOrganizationId,
        this.newChatInitialMessage || undefined
      );

      if (response?.success?.data) {
        const newChat = response.success.data;
        this.closeCreateChatModal();
        await this.loadChats();
        // Select the newly created chat
        const chatInList = this.chats.find(c => c._id === newChat._id);
        if (chatInList) {
          this.selectChat(chatInList);
        }
      }
    } catch (error) {
      console.error('Error creating chat:', error);
    } finally {
      this.isCreatingChat = false;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ⭐ THE CUSTOMER PANEL
  //
  //  Clicking the name in the chat header opens the exact panel the CEO users page
  //  opens on a row — profile, acquisition, spend, projects, plan, credits.
  //
  //  ⚠️ ACCOUNT-SCOPED CHATS ONLY. A project chat is keyed by `organizationId` and
  //  carries an email at best; only an account chat stores the `accountUserId` the
  //  panel is addressed by, and guessing one from an email is not a thing to do
  //  silently on a screen that can move money.
  // ═══════════════════════════════════════════════════════════════════════

  /** The account this chat belongs to, or null when there is no id to open. */
  detailUserIdOf(chat: SupportChat | null): string | null {
    return chat?.accountUserId || null;
  }

  openUserDetail(chat: SupportChat | null) {
    const userId = this.detailUserIdOf(chat);
    if (!userId) return;
    this.detailUserId = userId;
  }

  closeUserDetail() {
    this.detailUserId = null;
  }

  /** The panel gave or removed credits — the header badge is now stale. */
  onDetailCreditsChanged() {
    this.loadUserCredits();
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  ⭐⭐ A SOCIAL-SHARE CREDIT REQUEST STILL WAITING ON A HUMAN
  //
  //  Claiming credits for a post writes TWO messages: the request itself, as a
  //  `user` message, and then an automatic `assistant` acknowledgement — see
  //  `acknowledgementMessage` in totalum-platform's `social-share-store.ts`.
  //
  //  ⚠️⚠️ THAT SECOND MESSAGE IS WHY THESE REQUESTS LOOKED ANSWERED. `needsResponse`
  //  means "the last message is the customer's", and the acknowledgement is ours —
  //  so a request nobody had decided on sat in the list with no red dot, behind
  //  every chat that did have one. It promises a decision "within a working day".
  //
  //  ⚠️ MATCHED ONLY WHILE THE ACKNOWLEDGEMENT IS THE LAST MESSAGE, AND ONLY UNTIL
  //  SOMEBODY ACTS. Any reply, or "Mark responded", makes it false again — so this
  //  can never leave a chat red after it has been dealt with.
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * The acknowledgement's opening line, loosely. The credit figure and the sentences
   * under it are free to change; either half of this is enough to recognise it.
   *
   * ⚠️⚠️ IT IS MATCHED AGAINST THE FIRST LINE ONLY, WHICH IS LOAD-BEARING. Run over the
   * whole message it would also match an ordinary reply that happens to say "thanks
   * for sharing Totalum" — including the approval below this — and a chat answered
   * with those words would then sit in the unanswered list for ever, unclearable
   * except by "Mark responded".
   */
  private static readonly CLAIM_ACK_PATTERN =
    /thanks for sharing totalum|received your request for\s+\d+\s+credits/i;

  isAwaitingClaimDecision(chat: SupportChat | null): boolean {
    const messages = chat?.messages;
    if (!chat || !messages?.length || chat.markedAsResponded) return false;

    const last = messages[messages.length - 1];
    if (last.sender !== 'assistant') return false;

    const firstLine = (last.message || '').split('\n')[0];
    return SupportChatPage.CLAIM_ACK_PATTERN.test(firstLine);
  }

  /**
   * ⭐ THE ONE THE TEMPLATE ASKS. `needsResponse` as the server computes it, plus the
   * acknowledged-but-undecided credit requests it cannot see.
   */
  needsReply(chat: SupportChat | null): boolean {
    return !!chat && (!!chat.needsResponse || this.isAwaitingClaimDecision(chat));
  }

  /** True for the open conversation. What the composer switches on. */
  get isClaimPending(): boolean {
    return this.isAwaitingClaimDecision(this.selectedChat);
  }

  /**
   * ⭐ THE THREE ANSWERS A CREDIT REQUEST EVER GETS.
   *
   * ⚠️ THEY REPLACE THE AI DRAFT RATHER THAN JOINING IT. The draft is written from
   * the docs and cannot know whether the post is real, so on this one message it is
   * always wrong — and one that reads well is exactly the kind that gets sent.
   *
   * ⚠️ THEY FILL THE BOX; THEY DO NOT SEND. Approving a request means actually giving
   * the credits (the + beside the balance) — this only writes the sentence that says
   * so, and the agent still reads it before it goes.
   */
  readonly claimReplies: { label: string; text: string }[] = [
    {
      label: 'Credits sent',
      text: "I've just sent you the credits — thanks a lot for the post! 🙌 They're already in your balance.",
    },
    {
      label: 'Post not about Totalum',
      text:
        "Thanks for taking the time to post. We can't approve this one, though: the post doesn't really say anything about Totalum, " +
        "so it doesn't qualify for the credits. You're very welcome to post again with a genuine mention of what you're building and send it over.",
    },
    {
      label: 'Account too new / no engagement',
      text:
        "Thanks for sharing! We can't approve this one, though: the account it was posted from is brand new and the post hasn't had any " +
        "genuine engagement, and those don't qualify. If you post again from an established account we'll gladly take another look.",
    },
  ];

  /** Same contract as `useSuggestion` — the composer, focused, nothing sent. */
  useClaimReply(text: string) {
    this.currentMessage = text;

    setTimeout(() => {
      this.autoResizeTextarea();
      this.messageTextarea?.nativeElement?.focus();
    }, 0);
  }
}
